import { format } from 'date-fns';
import { ampli } from '../../ampli';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { useEffect, useState, useRef, useMemo } from "react";
import { API, ShareAPI } from "../../API";
import { useTranslation } from "react-i18next";
import { WidgetContentModal } from '../WidgetContentModal/WidgetContentModal';
import { AddToNarrativeModal } from '../AddToNarrativeModal/AddToNarrativeModal';
import { AddToSourceGroupModal } from '../AddToSourceGroupModal/AddToSourceGroupModal';
import { VerticalBarChart } from '../VerticalBarChart/VerticalBarChart';
import { showRequestError } from '../../utils/requestError';

const BAR_COLORS = ['#BEFDEE', '#7FFADD', '#45F1C9', '#1CD6AA', '#00BA8D'];

const audienceSizeMap = {
  'very_small': [0, 1000],
  'small': [1000, 10000],
  'medium': [10000, 100000],
  'big': [100000, 500000],
  'very_big': [500000, 1000000000]
};

export const AudienceChart = ({
  report,
  story,
  isShare,
  isClickable=true,
  sourceGroups,
  countries,
  countryGroups = [],
  startDate,
  endDate,
}) => {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const [audience, setAudience] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filter, setFilter] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  const api = isShare ? ShareAPI : API;
  const abortControllerRef = useRef();
  const [addToCaseModal, setAddToCaseModal] = useState({ isOpen: false, audience: null, sourceId: null });
  const [addToGroupModal, setAddToGroupModal] = useState({ isOpen: false, audience: null, sourceId: null });

  const bucketNameMap = useMemo(() => ({
    'very_small': t('Very small (< 1k)'),
    'small': t('Small (1k - 10k)'),
    'medium': t('Medium (10k - 100k)'),
    'big': t('Big (100k - 500k)'),
    'very_big': t('Very big (> 500k)')
  }), [t]);

  const getAudience = () => {
    if (!story) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const urlParams = new URLSearchParams();
    urlParams.append('breakdown', 'audience');
    if(sourceGroups.length > 0) {
      sourceGroups.forEach((sourceGroup) => {
        urlParams.append('source_group_ids', sourceGroup.value);
      });
    }
    if(countries.length > 0) {
      countries.forEach((country) => {
        urlParams.append('source_country_ids', country.value);
      });
    }
    if (countryGroups.length > 0) {
      countryGroups.forEach((groupId) => {
        urlParams.append('country_group_ids', groupId);
      });
    }
    if(startDate) {
      urlParams.append('date_from',format(startDate, 'yyyy-LL-dd'));
    }
    if(endDate) {
      urlParams.append('date_to',format(endDate, 'yyyy-LL-dd'));
    }

    // Create a new AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const signal = abortController.signal;

    api.fetch('GET', `/API/v1/topic-modeling/${report?.id}/events/${story?.id}/sources/breakdown?${urlParams.toString()}`, null, null, signal).then(
      data => setAudience(data)
    ).catch((error) => showRequestError(error, t('Audience size')))
  }

  useEffect(() => {
    getAudience()
  }, [story])

  const chartItems = useMemo(
    () =>
      (audience?.objects ?? []).map((item, index) => ({
        bucket: item.audience_size,
        name: bucketNameMap[item.audience_size] ?? item.audience_size,
        value: item.sources_count,
        color: BAR_COLORS[index % BAR_COLORS.length],
      })),
    [audience, bucketNameMap],
  );

  const onSelect = (index) => {
    const item = index === null ? undefined : chartItems[index];
    if (!item) return;
    setFilter(audienceSizeMap[item.bucket]);
    setModalTitle(item.name);
    setIsModalOpen(true);
    ampli.track({
      event_type: 'Open wiget popup in event',
      event_properties: {
        user_id: currentUser?.id,
        workspace_id: currentUser?.workspace_id,
        report_id: report?.id,
        popup_type: 'Actor Types: Audience',
        popup_value: 'Auduience size: ' + item.name,
      }
    });
  };

  return (
    <>
      {audience ? (
        <VerticalBarChart
          chartData={chartItems}
          isClickable={isClickable}
          onSelect={onSelect}
          seriesName={t('Actors')}
        />
      ) : (
        <VerticalBarChart chartData={null} />
      )}

      {isModalOpen ? (
        <WidgetContentModal
          infoModal={isModalOpen}
          isChart={true}
          title={modalTitle}
          platformOption={null}
          type={null}
          subType={null}
          stateAffiliatedId={null}
          countryId={countries?.length > 0 ? countries : null}
          // platformsOptions={platformOptions}
          audienceRange={filter}
          narrative={report}
          sourceGroup={sourceGroups.length > 0 ? sourceGroups?.map((sourceGroup) => sourceGroup.value) : null}
          source={null}
          sentimentOptions={null}
          eventIdsOptions={[story.id]}
          startDate={startDate}
          endDate={endDate}
          contentTypeOptions={null}
          isShare={isShare}
          onChange={(bool) => {
            setIsModalOpen(bool);
          }}
          showAddToActions={true}
          onAddToCase={(bool, sourceId) => {
            setAddToCaseModal({
              isOpen: bool,
              audience: filter,
              sourceId: sourceId,
            });
          }}
          onAddToGroup={(bool, sourceId) => {
            setAddToGroupModal({
              isOpen: bool,
              audience: filter,
              sourceId: sourceId,
            });
          }}
        />
      ) : (
        ''
      )}

      {addToCaseModal.isOpen ? (
        <AddToNarrativeModal
          isOpen={addToCaseModal.isOpen}
          deduplication={false}
          caseId={report?.id}
          eventIds={[story?.id]}
          areAllMessagesSelected={true}
          startDate={startDate}
          endDate={endDate}
          sourceGroups={sourceGroups}
          sources={addToCaseModal.sourceId ? [addToCaseModal.sourceId] : []}
          countries={countries}
          audienceRange={addToCaseModal.audience}
          onChange={(bool) => {
            setAddToCaseModal({
              isOpen: bool,
              contentTypes: null,
              sentiment: null,
              sourceId: null,
            });
          }}
        />
      ) : (
        ''
      )}
      {addToGroupModal.isOpen ? (
        <AddToSourceGroupModal
          isOpen={addToGroupModal.isOpen}
          deduplication={false}
          areAllMessagesSelected={true}
          startDate={startDate}
          endDate={endDate}
          sourceGroups={sourceGroups}
          sourcesFilter={addToGroupModal.sourceId ? [addToGroupModal.sourceId] : []}
          countries={countries}
          audienceRange={addToGroupModal.audience}
          narrative={report}
          eventIds={[story?.id]}
          onChange={(bool) => {
            setAddToGroupModal({
              isOpen: bool,
              contentTypes: null,
              sentiment: null,
              sourceId: null,
            });
          }}
        />
      ) : (
        ''
      )}
    </>
  );
};